import React from 'react';
import {
  FaChevronRight,
  FaHistory,
  FaKey,
  FaMagic,
  FaTimes,
} from 'react-icons/fa';
import { BtnSimple } from '../form/btn.Simple';
import { InputSimple } from '../form/input.Simple';
import { NavigationManagePassword } from '../navigation/navigation.Passwords';
import { NavigationDisplayItems } from '../navigation/navigation.displayItems';
import { ThemeChange } from '../theme/theme.Change';
import { getAvatarBackgroundStyle, getItemPicAsset } from '../../utils/item';
import { renderIconBySlug } from '../../content/icons';
import { getTemplatePathLabel } from '../../utils/templateLibrary';

export const ItemAvatar = ({ item, size = 36 }) => {
  const avatarAsset = getItemPicAsset(item, 0);
  const hasIcon = Boolean(avatarAsset.iconSlug);
  const useImage = Boolean(avatarAsset.url) && !hasIcon;
  const bgStyle = avatarAsset.bgColor
    ? getAvatarBackgroundStyle(avatarAsset.bgColor)
    : { backgroundColor: 'rgba(var(--main), 0.35)' };

  return (
    <div
      className='shrink-0 rounded-full bg-main bg-opacity-40 flex items-center justify-center overflow-hidden'
      style={{
        width: size,
        height: size,
        ...(useImage
          ? { background: `url(${avatarAsset.url}) no-repeat center / cover` }
          : bgStyle),
      }}
    >
      {hasIcon &&
        renderIconBySlug(avatarAsset.iconSlug, {
          size: Math.max(18, Math.round(size * 0.62)),
          color: 'var(--contrast)',
        })}
      {!hasIcon && !useImage && (
        <span className='text-xs font-bold text-contrast text-opacity-60'>
          {item?.label?.[0]?.toUpperCase() || '?'}
        </span>
      )}
    </div>
  );
};

export const OpenItemChevron = ({ itemId, title, onOpen }) => {
  const handleOpen = event => {
    event.stopPropagation();
    onOpen(itemId);
  };

  const handleKeyDown = event => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    onOpen(itemId);
  };

  if (!itemId) {
    return null;
  }

  return (
    <span
      role='button'
      tabIndex={0}
      className='shrink-0 text-contrast text-opacity-60 hover:text-secondary'
      title={title}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
    >
      <FaChevronRight size={12} />
    </span>
  );
};

export const TemplateCard = ({ template, selected, onSelect, onOpen }) => (
  <button
    type='button'
    className={`w-full rounded-lg p-1 text-left shadow transition hover:opacity-90 ${
      selected
        ? 'bg-secondary bg-opacity-20'
        : 'bg-main bg-opacity-50 hover:bg-opacity-70'
    }`}
    onClick={() => onSelect(template)}
  >
    <div className='flex items-center justify-between gap-1'>
      <ItemAvatar
        item={template}
        size={46}
      />
      <div className='min-w-0 flex-1'>
        <div className='truncate font-condensed text-lg font-bold text-contrast'>
          {template.label}
        </div>
        <div className='text-xs text-contrast text-opacity-60'>
          {getTemplatePathLabel(template)}
        </div>
      </div>
      <OpenItemChevron
        itemId={template?._id}
        title='Ouvrir la page du template'
        onOpen={() => onOpen(template)}
      />
    </div>
  </button>
);

export const TargetOption = ({
  item,
  title,
  subtitle,
  selected,
  onSelect,
  onOpen,
  openTitle,
  openItemId,
  className = '',
  avatarSize = 42,
}) => (
  <button
    type='button'
    className={`${className} flex w-full items-center gap-1 rounded-lg px-1 py-05 text-left transition hover:bg-secondary hover:bg-opacity-10 ${
      selected ? 'bg-secondary bg-opacity-10 text-secondary' : ''
    }`}
    onClick={onSelect}
  >
    <ItemAvatar
      item={item || { label: title }}
      size={avatarSize}
    />
    <span className='min-w-0 flex-1'>
      <span className='block truncate'>{title}</span>
      {subtitle && (
        <span className='block truncate text-xs text-contrast text-opacity-50'>
          {subtitle}
        </span>
      )}
    </span>
    <OpenItemChevron
      itemId={openItemId || item?._id}
      title={openTitle}
      onOpen={onOpen}
    />
  </button>
);

export const HistorySidebar = ({
  navigationSize,
  recentItems,
  onOpenPasswords,
}) => (
  <div
    className='flex h-full min-h-0 flex-col overflow-hidden border-l border-main-d border-opacity-20 bg-main'
    style={{
      width: navigationSize || 200,
      minWidth: navigationSize || 200,
    }}
  >
    <div className='sticky top-0 flex items-center gap-1 bg-main bg-opacity-50 px-05 font-condensed text-lg font-bold backdrop-blur-md'>
      <FaHistory />
      Historique
    </div>
    <div className='flex-1 overflow-auto'>
      {recentItems.length > 0 ? (
        recentItems.map(item => (
          <NavigationDisplayItems
            key={item?._id}
            displayItem={item}
            big={true}
            navigateOnClick={true}
            hideChevron={true}
          />
        ))
      ) : (
        <div className='p-1 text-xs text-contrast text-opacity-60'>
          Aucun historique.
        </div>
      )}
    </div>
    <div className='sticky bottom-0 z-10 flex w-full justify-evenly gap-1 border-t border-main-d bg-main py-05'>
      <ThemeChange />
      <NavigationManagePassword />
      <div
        className='cursor-pointer transition hover:opacity-50'
        onClick={onOpenPasswords}
      >
        <FaKey />
      </div>
    </div>
  </div>
);

export const TemplateListSection = ({
  loading,
  search,
  onSearchChange,
  templates,
  selectedTemplateId,
  onSelectTemplate,
  onOpenItem,
}) => (
  <section className='rounded-lg bg-main bg-opacity-40 p-1 shadow backdrop-blur-sm lg:w-1/3'>
    <div className='mb-1 flex items-center gap-05 font-condensed text-sm font-bold uppercase text-contrast text-opacity-80'>
      <span className='flex-1'>Templates accessibles</span>
      <span className='text-xs text-contrast text-opacity-50'>
        {templates.length}
      </span>
    </div>
    <InputSimple
      className='m-0 mb-1 w-full'
      placeholder='Rechercher un template ou un dossier'
      resetValue={search}
      onChange={event => onSearchChange(event.target.value)}
    />

    {loading ? (
      <div className='p-2 text-sm text-contrast text-opacity-70'>
        Chargement des templates...
      </div>
    ) : templates.length === 0 ? (
      <div className='p-2 text-sm text-contrast text-opacity-70'>
        Aucun template accessible.
      </div>
    ) : (
      <div className='flex flex-wrap gap-1'>
        {templates.map(template => (
          <div
            key={template._id}
            className='w-full'
          >
            <TemplateCard
              template={template}
              selected={selectedTemplateId === template._id}
              onSelect={onSelectTemplate}
              onOpen={templateItem => onOpenItem(templateItem?._id)}
            />
          </div>
        ))}
      </div>
    )}
  </section>
);

export const TemplateCreatePanel = ({
  selectedTemplate,
  newItemLabel,
  targetSearch,
  universeOptions,
  selectedUniverseId,
  selectedUniverseTarget,
  selectedUniverseCampaignTargets,
  filteredTargets,
  selectedTargetId,
  selectedTarget,
  targets,
  creating,
  createButtonLabel,
  onClearTemplate,
  onNameChange,
  onTargetSearchChange,
  onSelectUniverse,
  onSelectTarget,
  onOpenItem,
  onCreate,
}) => (
  <aside className='rounded-lg bg-main bg-opacity-40 p-1 shadow backdrop-blur-sm lg:flex-1'>
    <div className='mb-1 flex items-center gap-05 font-condensed text-sm font-bold uppercase text-contrast text-opacity-80'>
      <FaMagic className='text-secondary' />
      <span>Créer depuis un template</span>
    </div>
    {selectedTemplate ? (
      <div className='mt-1 rounded-lg bg-secondary bg-opacity-10 p-1 shadow'>
        <div className='flex items-start justify-between gap-1'>
          <ItemAvatar
            item={selectedTemplate}
            size={42}
          />
          <div className='min-w-0 flex-1'>
            <div className='text-xs uppercase text-secondary'>
              Template sélectionné
            </div>
            <div className='truncate font-bold'>{selectedTemplate.label}</div>
            <div className='text-xs text-contrast text-opacity-60'>
              {getTemplatePathLabel(selectedTemplate)}
            </div>
          </div>
          <button
            type='button'
            className='shrink-0 text-contrast text-opacity-60 hover:text-secondary'
            title='Retirer le template'
            onClick={onClearTemplate}
          >
            <FaTimes />
          </button>
        </div>
      </div>
    ) : (
      <div className='mt-1 rounded-lg bg-main bg-opacity-30 p-1 text-sm text-contrast text-opacity-70'>
        Sélectionne un template dans la liste.
      </div>
    )}

    <label className='mt-1 block text-sm'>
      <span className='mb-03 block font-condensed text-xs font-bold uppercase text-contrast text-opacity-70'>
        Nom du nouvel item
      </span>
      <InputSimple
        className='m-0 w-full'
        placeholder='Nom du nouvel item'
        resetValue={newItemLabel}
        onChange={event => onNameChange(event.target.value)}
      />
    </label>

    <div className='mt-1 flex flex-col gap-1 xl:flex-row'>
      <div className='text-sm xl:w-1/2'>
        <span className='mb-03 block font-condensed text-xs font-bold uppercase text-contrast text-opacity-70'>
          Univers cible
        </span>
        {universeOptions.length > 0 ? (
          <div
            className='gap-1 rounded-lg bg-main bg-opacity-30 p-05 overflow-auto'
            style={{ maxHeight: 465 }}
          >
            {universeOptions.map(universe => (
              <TargetOption
                key={universe.id}
                item={universe.item}
                openItemId={universe.id}
                title={universe.label}
                subtitle={
                  !universe.canCreateInUniverse ? 'Dossier requis' : ''
                }
                selected={selectedUniverseId === universe.id}
                onSelect={() => onSelectUniverse(universe)}
                onOpen={onOpenItem}
                openTitle="Ouvrir la page de l'univers"
              />
            ))}
          </div>
        ) : (
          <p className='text-sm text-contrast text-opacity-70'>
            Aucun univers disponible avec une permission explicite.
          </p>
        )}
      </div>

      <div className='text-sm xl:w-1/2'>
        <span className='mb-03 block font-condensed text-xs font-bold uppercase text-contrast text-opacity-70'>
          Dossier cible optionnel
        </span>
        {selectedUniverseTarget && (
          <TargetOption
            className={`mb-05 ${
              selectedTargetId === selectedUniverseTarget._id
                ? ''
                : 'bg-main bg-opacity-30'
            }`}
            item={selectedUniverseTarget}
            title={`Créer directement dans ${selectedUniverseTarget.label}`}
            subtitle='Aucun dossier spécifique'
            selected={selectedTargetId === selectedUniverseTarget._id}
            onSelect={() => onSelectTarget(selectedUniverseTarget._id)}
            onOpen={onOpenItem}
            openTitle="Ouvrir la page de l'univers"
            avatarSize={40}
          />
        )}
        <InputSimple
          className='m-0 w-full'
          placeholder='Rechercher un dossier dans cet univers'
          resetValue={targetSearch}
          onChange={event => onTargetSearchChange(event.target.value)}
        />
        {selectedUniverseId && selectedUniverseCampaignTargets.length > 0 && (
          <div
            className='gap-1 rounded-lg bg-main bg-opacity-30 p-05 overflow-auto'
            style={{ maxHeight: 330 }}
          >
            {filteredTargets.length === 0 ? (
              <div className='p-1 text-xs text-contrast text-opacity-60'>
                Aucun dossier cible trouvé.
              </div>
            ) : (
              filteredTargets.map(target => (
                <TargetOption
                  key={target._id}
                  item={target}
                  title={target.label}
                  subtitle={getTemplatePathLabel(target)}
                  selected={selectedTargetId === target._id}
                  onSelect={() => onSelectTarget(target._id)}
                  onOpen={onOpenItem}
                  openTitle='Ouvrir la page du dossier'
                />
              ))
            )}
          </div>
        )}
        {selectedUniverseId &&
          !selectedUniverseTarget &&
          selectedUniverseCampaignTargets.length === 0 && (
            <p className='mt-05 text-xs text-contrast text-opacity-60'>
              Aucun dossier cible disponible dans cet univers.
            </p>
          )}
      </div>
    </div>

    {targets.length === 0 && (
      <p className='mt-1 text-sm text-contrast text-opacity-70'>
        Aucun dossier cible disponible avec une permission explicite owner,
        admin ou éditeur.
      </p>
    )}

    <BtnSimple
      className='mt-2 flex w-full items-center justify-center gap-05'
      disabled={
        creating || !selectedTemplate || !selectedTarget || !newItemLabel.trim()
      }
      onClick={onCreate}
    >
      <FaMagic />
      {createButtonLabel}
    </BtnSimple>
  </aside>
);
